<template>
<div class="px-6 pb-6">
  <v-card class="rounded-lg px-6 py-6" elevation="0" style="border: 1px solid #e0e0e0;">

    <!-- Header -->
    <div class="d-flex align-center justify-space-between mb-6 flex-wrap">
      <div>
        <h2 class="text-h5 font-weight-bold grey--text text--darken-3 mb-1">Библиотека шаблонов заметок</h2>
        <div class="text-body-2 grey--text">Всего: {{ filteredNotes.length }}</div>
      </div>
      <v-btn
        color="#009E60"
        dark
        depressed
        class="text-capitalize mt-2 mt-sm-0 px-6"
        height="40"
        @click="openNoteModal(null)"
      >
        <v-icon left small>mdi-plus</v-icon>
        Новый шаблон
      </v-btn>
    </div>

    <!-- Filters -->
    <div class="d-flex mb-4" style="gap: 12px; flex-wrap: wrap;">
      <v-text-field
        v-model="search"
        outlined
        dense
        hide-details
        placeholder="Поиск по тексту шаблона"
        prepend-inner-icon="mdi-magnify"
        style="max-width: 560px; flex: 1;"
        class="ln-search"
      />
      <v-select
        v-model="filterAssignment"
        :items="assignmentOptions"
        item-text="label"
        item-value="value"
        outlined
        dense
        hide-details
        style="max-width: 220px; min-width: 160px;"
        class="ln-select"
      />
      <v-select
        v-model="filterKind"
        :items="kindOptions"
        item-text="label"
        item-value="value"
        outlined
        dense
        hide-details
        style="max-width: 220px; min-width: 160px;"
        class="ln-select"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-center py-10">
      <v-progress-circular indeterminate color="#009E60"/>
    </div>

    <!-- Empty -->
    <div v-else-if="!filteredNotes.length" class="d-flex flex-column align-center py-10">
      <v-icon size="48" color="grey lighten-2">mdi-note-text-outline</v-icon>
      <p class="text-body-2 grey--text mt-3">Нет шаблонов. Нажмите «Новый шаблон».</p>
    </div>

    <!-- List -->
    <div v-else class="ln-list">
      <div
        v-for="item in filteredNotes"
        :key="item.id"
        class="ln-row"
      >
        <div class="ln-row__main">
          <div class="d-flex align-center flex-wrap mb-1" style="gap: 8px;">
              <span class="ln-chip" :class="`ln-chip--${item.kind}`">
                {{ kindLabel(item.kind) }}
              </span>
              <span class="ln-chip ln-chip--assignment">
                {{ assignmentLabel(item.assignment) }}
              </span>
          </div>
          <p class="ln-row__text mb-0">{{ item.text }}</p>
        </div>
        <div class="ln-row__actions">
          <v-btn icon small @click="openNoteModal(item.id)">
            <v-icon size="18" color="#9BA1B5">mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn icon small @click="deleteItem(item)">
            <v-icon size="18" color="#EC2E2E">mdi-trash-can-outline</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

  </v-card>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

const KIND_OPTIONS = [
  { value: null, label: 'Все типы' },
  { value: 'strength', label: 'Сильные стороны' },
  { value: 'growth_area', label: 'Зоны роста' },
  { value: 'recommendation', label: 'Рекомендации' },
];

const ASSIGNMENT_OPTIONS = [
  { value: null, label: 'Все назначения' },
  { value: 'result', label: 'Итоги ассессора' },
  { value: 'competence', label: 'По компетенции' },
];

export default {
  name: 'LibraryNotes',
  data: () => ({
    search: '',
    filterKind: null,
    filterAssignment: null,
    kindOptions: KIND_OPTIONS,
    assignmentOptions: ASSIGNMENT_OPTIONS,
  }),
  computed: {
    ...mapGetters({
      notes: 'diagnostics/diagnotics/libraryNotes',
      loading: 'diagnostics/diagnotics/libraryNotesLoading',
    }),
    filteredNotes() {
      let items = this.notes;
      if (this.search.trim()) {
        const s = this.search.toLowerCase();
        items = items.filter(n => n.text && n.text.toLowerCase().includes(s));
      }
      if (this.filterKind !== null) {
        items = items.filter(n => n.kind === this.filterKind);
      }
      if (this.filterAssignment !== null) {
        items = items.filter(n =>
          this.filterAssignment === 'result'
            ? (n.assignment === 'result' || !n.assignment)
            : n.assignment === this.filterAssignment
        );
      }
      return items;
    },
  },
  methods: {
    ...mapActions({
      openEditNote: 'diagnostics/diagnotics/openEditNote',
      openCreateNote: 'diagnostics/diagnotics/openCreateNote',
      deleteNoteTemplate: 'diagnostics/diagnotics/deleteNoteTemplate',
    }),
    kindLabel(kind) {
      const found = KIND_OPTIONS.find(o => o.value === kind);
      return found ? found.label : kind;
    },
    assignmentLabel(assignment) {
      return assignment === 'competence' ? 'По компетенции' : 'Итоги ассессора';
    },
    openNoteModal(id) {
      if (id) {
        this.openEditNote(id);
      } else {
        this.openCreateNote();
      }
    },
    async deleteItem(item) {
      await this.deleteNoteTemplate(item.id);
    },
  },
};
</script>

<style scoped lang="scss">
.ln-search,
.ln-select {
  ::v-deep .v-input__slot {
    border-radius: 8px !important;

    fieldset {
      border-color: #DEE1EA !important;
    }
  }
}

.ln-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #EEEEEE;
  border-radius: 8px;
  overflow: hidden;
}

.ln-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #F5F5F5;
  transition: background .12s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #FAFAFA;
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__text {
    font-size: 14px;
    color: #25325F;
    line-height: 1.5;
    white-space: pre-line;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    padding-top: 2px;
  }
}

.ln-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  &--strength {
    background: #E8F5EE;
    color: #00794A;
  }

  &--growth_area {
    background: #FFF4E5;
    color: #B76D00;
  }

  &--recommendation {
    background: #EEF0FF;
    color: #483CB9;
  }

  &--assignment {
    background: #F5F5F5;
    color: #757575;
  }
}
</style>
